import { memo, useCallback, type ChangeEvent } from 'react'
import { ChevronDown, Moon, RadioTower, Search, Sun, Tv } from 'lucide-react'
import { useServerClock } from '../../hooks/useServerClock.js'
import type { Theme } from '../../hooks/useTheme.js'
import type { Channel } from '../../types/index.js'
import { AccountMenu } from '../auth/AccountMenu.js'
import { UpdateControl } from '../maintenance/UpdateControl.js'
import { NotificationCenter } from './NotificationCenter.js'
import type { DataAvailabilityState } from '../../lib/data-availability.js'

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
}

function formatClock(date: Date) {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`
}

interface AppHeaderProps {
  channels:        Channel[]
  activeChannelId: string | null
  onChannelChange: (id: string) => void
  onOpenSearch:    () => void
  theme:           Theme
  onToggleTheme:   () => void
  wsConnected:     boolean
  bridgeConnected: boolean
  engineLabel:     string | null
  activeTargetName?: string | null
  backupHealthy?: boolean | null
  dataAvailability: DataAvailabilityState
  realtimeRequired: boolean
}

const AVAILABILITY_STATUS_COPY: Partial<Record<DataAvailabilityState, { label: string; title: string }>> = {
  loading: {
    label: 'CHARGEMENT',
    title: 'Actualisation de l’état du serveur',
  },
  reconnecting: {
    label: 'RECONNEXION',
    title: 'Flux temps réel interrompu — reconnexion automatique en cours',
  },
  offline: {
    label: 'RÉSEAU HS',
    title: 'Connexion réseau du navigateur indisponible',
  },
  'clock-unavailable': {
    label: 'HORLOGE HS',
    title: 'Mesure de l’horloge backend temporairement indisponible',
  },
  'server-unavailable': {
    label: 'SERVEUR HS',
    title: 'Serveur TrafficPlay temporairement indisponible',
  },
}

export const AppHeader = memo(function AppHeader({
  channels,
  activeChannelId,
  onChannelChange,
  onOpenSearch,
  theme,
  onToggleTheme,
  wsConnected,
  bridgeConnected,
  engineLabel,
  activeTargetName,
  backupHealthy,
  dataAvailability,
  realtimeRequired,
}: AppHeaderProps) {
  const { now, status, offsetMs, rttMs } = useServerClock()
  const activeChannel = channels.find(channel => channel.id === activeChannelId) ?? null

  const handleChannelSelect = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => onChannelChange(event.target.value),
    [onChannelChange],
  )

  // Trois états et non deux : un WebSocket coupé et un CasparCG coupé ne se
  // corrigent pas de la même façon, l'opérateur doit pouvoir les distinguer.
  const playoutState = !realtimeRequired && dataAvailability === 'ready'
    ? 'server'
    : ['loading', 'reconnecting', 'clock-unavailable'].includes(dataAvailability)
      ? 'loading'
      : dataAvailability !== 'ready' || !wsConnected
        ? 'offline'
        : bridgeConnected && backupHealthy !== false ? 'ok' : 'degraded'
  const availabilityCopy = AVAILABILITY_STATUS_COPY[dataAvailability]
  const playoutLabel = availabilityCopy?.label ?? (playoutState === 'ok'
    ? `${activeTargetName ?? 'PLAYOUT'} OK`
    : playoutState === 'server'
      ? 'SERVEUR OK'
      : playoutState === 'degraded'
        ? bridgeConnected ? 'SECOURS HS' : 'CASPAR HS'
        : 'CHARGEMENT')
  const playoutTitle = availabilityCopy?.title ?? (playoutState === 'ok'
    ? 'Moteur de diffusion connecté'
    : playoutState === 'server'
      ? 'Serveur TrafficPlay joignable'
      : playoutState === 'degraded'
        ? bridgeConnected ? 'Playout à l’antenne sain, secours indisponible' : 'TrafficPlay connecté, CasparCG injoignable'
        : 'Actualisation de l’état du serveur')

  const clockTitle = status === 'SYNCED'
    ? `Horloge synchronisée sur le backend · offset ${Math.round(offsetMs)} ms${rttMs != null ? ` · RTT ${Math.round(rttMs)} ms` : ''}`
    : 'Horloge backend indisponible — valeur masquée'

  return (
    <header className="tp-appbar">
      <div className="tp-appbar__brand">
        <span className="tp-appbar__brand-mark" aria-hidden="true">
          <RadioTower size={15} />
        </span>
        <span className="tp-appbar__brand-copy">
          <span className="tp-appbar__brand-name">TRAFFICPLAY</span>
          <span className="tp-appbar__brand-meta">RÉGIE DE DIFFUSION</span>
        </span>
      </div>

      <div className="tp-appbar__channel">
        <Tv size={15} aria-hidden="true" />
        {channels.length > 1 ? (
          <>
            <select
              className="tp-appbar__channel-select"
              value={activeChannelId ?? ''}
              onChange={handleChannelSelect}
              aria-label="Chaîne active"
            >
              {channels.map(channel => (
                <option key={channel.id} value={channel.id}>{channel.name.toUpperCase()}</option>
              ))}
            </select>
            <ChevronDown size={13} aria-hidden="true" className="tp-appbar__channel-chevron" />
          </>
        ) : (
          <span className="tp-appbar__channel-label">{(activeChannel?.name ?? '—').toUpperCase()}</span>
        )}
      </div>

      <button type="button" className="tp-appbar__search" onClick={onOpenSearch} aria-label="Recherche globale">
        <Search size={14} aria-hidden="true" />
        <span className="tp-appbar__search-placeholder">Programme, média, spot, template…</span>
        <kbd className="tp-appbar__search-kbd">⌘K</kbd>
      </button>

      <div className="tp-appbar__end">
        <UpdateControl />
        <NotificationCenter />
        <AccountMenu />
        <div className="tp-appbar__theme" role="group" aria-label="Thème de l’interface">
          <button
            type="button"
            className={`tp-appbar__theme-button${theme === 'clair' ? ' is-active' : ''}`}
            onClick={theme === 'clair' ? undefined : onToggleTheme}
            aria-pressed={theme === 'clair'}
            title="Thème clair — préparation de grille"
            aria-label="Thème clair"
          >
            <Sun size={14} aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`tp-appbar__theme-button${theme === 'antenne' ? ' is-active' : ''}`}
            onClick={theme === 'antenne' ? undefined : onToggleTheme}
            aria-pressed={theme === 'antenne'}
            title="Thème antenne — régie sombre"
            aria-label="Thème antenne"
          >
            <Moon size={14} aria-hidden="true" />
          </button>
        </div>

        <div className="tp-appbar__clock" title={clockTitle}>
          <div className="tp-appbar__clock-time">{status === 'SYNCED' ? formatClock(now) : '--:--:--'}</div>
          <div className="tp-appbar__clock-date">
            {status === 'SYNCED'
              ? `${now.toLocaleDateString('fr-FR', DATE_FORMAT).replace(/\./g, '').toUpperCase()} · PARIS`
              : 'CHARGEMENT HORLOGE'}
          </div>
        </div>

        <div className={`tp-appbar__playout is-${playoutState}`} title={playoutTitle}>
          <span className="tp-appbar__playout-dot" aria-hidden="true" />
          <div>
            <div className="tp-appbar__playout-label">{playoutLabel}</div>
            <div className="tp-appbar__playout-detail">{engineLabel ?? 'MOTEUR —'}</div>
          </div>
        </div>
      </div>
    </header>
  )
})
